/**
 * Cheers (docs/cheers.md, flag `cheersUI`). Owner, 2026-10-28: "There need to be more ways which
 * encourage people to do assigned chores", picking from a short list: "Anyone can tap a cheer on a
 * finished task, and the person who did it gets 'Hue cheered Run Dishwasher'. You get thanked right
 * away instead of waiting for Sunday." Then, of the first cut's quiet word: "it should not be a toggle
 * at all and it should not be quiet and hidden in the task screen". So a cheer is loud, for keeps, and
 * comes to the person in a sheet over the board (docs/cheers.md).
 *
 * The rules, the words and the icon both clients and the server use: who may cheer a done task, the
 * optimistic answer to a tap, the line saying who cheered, the push's copy and the refusals. Who the
 * cheer row belongs to and the push once ever are server/cheers.ts. Pure, so the Node tests import it
 * by extension. Names compare as everywhere a name is compared: trimmed, inner spaces collapsed, case
 * ignored (shared/notes.ts `sameName`).
 */
import { sameName, HEART_POP_FRAME_MS, HEART_POP_FRAMES } from "./notes.ts";
import { normalizeName } from "./peons.ts";

/** The task fields the rules read. */
export interface CheerTask {
  status: string;
  done_by: string | null;
  /** Who cheered it, in the order they did (server/db.ts TASK_SELECT); absent on an older row. */
  cheers?: ReadonlyArray<string> | null;
}

/**
 * The written words. Plain, as the app speaks; nothing here is said aloud. A cheer is loud and for
 * keeps (owner, 2026-10-28, of the first cut's quiet word: "it should not be a toggle at all and it
 * should not be quiet and hidden in the task screen"): a gold button, confetti, no taking it back.
 */
export const CHEER_WORDS = {
  /** The gold button, on the cheer sheet and on a done task's screen. */
  button: "Cheer!",
  /** The same button once your cheer is in, while the confetti falls; it takes no more presses. */
  sent: "Cheered!",
  /** The cheer sheet's title bar: someone else finished a job. */
  jobTitle: "Job's done!",
  /** Under the cheer sheet's button: closes it, every job on it passed by on this device. */
  later: "Later",
  /** The doer's sheet's title bar. */
  cheeredTitle: "Cheered!",
  /** The doer's sheet's button. */
  cheeredButton: "Nice!",
  /** Over the row of cheering peons on a done task's screen. */
  crowd: "Cheered by",
} as const;

/** Whether `me` has cheered: names match ignoring case and spacing. */
export function hasCheered(cheers: ReadonlyArray<string> | null | undefined, me: string): boolean {
  return (cheers ?? []).some((n) => sameName(n, me));
}

/**
 * Whether `me` may cheer this task: it is done, and `me` is someone with a name who is not the peon it
 * is credited to (`done_by`, after any Give credit). Not on an open task; an abandoned or removed one
 * is no task to cheer. The server refuses the same (server/cheers.ts).
 */
export function canCheer(task: CheerTask, me: string): boolean {
  if (task.status !== "done" || !task.done_by) return false;
  return normalizeName(me).length > 0 && !sameName(task.done_by, me);
}

/** The cheers once `me` has cheered: theirs added at the end, once. The optimistic answer to a tap; there is no taking it back. */
export function addCheer(cheers: ReadonlyArray<string> | null | undefined, me: string): string[] {
  const cur = [...(cheers ?? [])];
  return hasCheered(cur, me) ? cur : [...cur, normalizeName(me)];
}

/**
 * The peons in the crowd on a done task, in the order they cheered: everyone who cheered but the peon
 * who now holds the credit (Give credit moved the job to someone who had cheered it: nobody cheers their
 * own job). Each is drawn as their peon stamp.
 */
export function cheerCrowd(cheers: ReadonlyArray<string> | null | undefined, doneBy: string | null | undefined): string[] {
  return (cheers ?? []).filter((n) => !(doneBy && sameName(n, doneBy)));
}

/**
 * Who cheered, in words, for a screen reader over the crowd and as the doer's sheet says it: "Hue",
 * "Hue and Jeff", "Hue, Jeff and Bones", and from four on "Hue, Jeff and 2 more". The reader is "you"
 * and comes first. "" for nobody.
 */
export function cheerNames(names: ReadonlyArray<string>, me: string): string {
  const others = names.filter((n) => !sameName(n, me));
  const all = others.length < names.length ? ["you", ...others] : [...others];
  if (all.length === 0) return "";
  if (all.length === 1) return all[0]!;
  if (all.length === 2) return `${all[0]} and ${all[1]}`;
  if (all.length === 3) return `${all[0]}, ${all[1]} and ${all[2]}`;
  return `${all[0]}, ${all[1]} and ${all.length - 2} more`;
}

/**
 * The crowd's name for a screen reader: "Cheered by Hue and Jeff", "Cheered by you", "" for nobody (no
 * crowd is drawn). Kept beside the stamps, which carry no text.
 */
export function cheersLine(cheers: ReadonlyArray<string> | null | undefined, doneBy: string | null | undefined, me: string): string {
  const names = cheerNames(cheerCrowd(cheers, doneBy), me);
  return names ? `${CHEER_WORDS.crowd} ${names}` : "";
}

/** The gold button's name for a screen reader: "Cheer Hue". */
export function cheerButtonName(doneBy: string | null | undefined): string {
  const who = typeof doneBy === "string" ? normalizeName(doneBy) : "";
  return who ? `Cheer ${who}` : "Cheer";
}

/** The cheer sheet's line under the photo: "Hue did Run Dishwasher". */
export function cheerJobLine(doneBy: string, title: string): string {
  return `${normalizeName(doneBy)} did ${title}`;
}

/** The doer's sheet's line for one job: "Hue and Jeff cheered Run Dishwasher". */
export function cheeredLine(from: ReadonlyArray<string>, title: string, me: string): string {
  return `${cheerNames(from, me)} cheered ${title}`;
}

// ---------- the sheets (docs/cheers.md "The cheer sheet", "The doer's sheet") ----------

/** A job done by someone else in this long that you have not cheered comes up on your cheer sheet. */
export const CHEER_JOB_WINDOW_MS = 24 * 3_600_000;
/** A cheer on your job in this long comes up on your sheet. */
export const CHEERED_WINDOW_MS = 3 * 24 * 3_600_000;
/**
 * Which finished jobs earn the cheer sheet (owner, 2026-10-30: "The cheer card opening when you open the
 * app should show completed tasks that don't get completed often or have waited a while. The idea is we
 * don't want normal chores constantly spamming people that they're done. If there's chore rate is low
 * from someone, that also makes it more likely to appear for others when they do something"). Points:
 * an uncommon job (its title done CHEER_RARE_MAX times or fewer in the last CHEER_RARE_WINDOW_MS, so
 * weekly or rarer) 2; a wait of CHEER_LONG_WAIT_MS 2, of CHEER_WAIT_MS 1; a doer whose completions in
 * the last CHEER_RATE_WINDOW_MS are under CHEER_LOW_RATE of the house's per-peon average 1. It shows at
 * CHEER_WORTH points. An everyday chore stays quiet unless it sat for days, or sat a while and a
 * peon who rarely helps did it.
 */
export const CHEER_RARE_WINDOW_MS = 30 * 24 * 3_600_000;
export const CHEER_RARE_MAX = 4;
export const CHEER_WAIT_MS = 2 * 24 * 3_600_000;
export const CHEER_LONG_WAIT_MS = 5 * 24 * 3_600_000;
export const CHEER_RATE_WINDOW_MS = 14 * 24 * 3_600_000;
export const CHEER_LOW_RATE = 0.5;
export const CHEER_WORTH = 2;

/** A finished job's facts for cheerPoints. */
export interface CheerFacts {
  /** Completions of the same title in the rare window, this one included. */
  titleCount: number;
  /** From when it was posted to when it was done, ms. */
  waitedMs: number;
  /** The doer's completions in the rate window, and the house's per-peon average there. */
  doerCount: number;
  houseAverage: number;
}

export function cheerPoints(f: CheerFacts): number {
  let points = 0;
  if (f.titleCount <= CHEER_RARE_MAX) points += 2;
  if (f.waitedMs >= CHEER_LONG_WAIT_MS) points += 2;
  else if (f.waitedMs >= CHEER_WAIT_MS) points += 1;
  if (f.houseAverage > 0 && f.doerCount < f.houseAverage * CHEER_LOW_RATE) points += 1;
  return points;
}

/** Whether a finished job comes up on the cheer sheet. */
export function cheerWorthy(f: CheerFacts): boolean {
  return cheerPoints(f) >= CHEER_WORTH;
}

/** The most jobs one cheer sheet shows, newest first; older ones are passed by quietly. */
export const CHEER_SHEET_MAX = 3;
/** The most jobs the doer's sheet names, newest first. */
export const CHEERED_SHEET_MAX = 3;

/** One cheer on one of your jobs, as GET /api/cheers/news answers it. */
export interface CheerReceived {
  task_id: string;
  title: string;
  from_name: string;
  /** ISO UTC. */
  created_at: string;
}

/** GET /api/cheers/news: jobs others did in the window that `me` has not cheered (newest first), and cheers on `me`'s jobs in theirs. */
export interface CheerNews<T> {
  jobs: T[];
  cheered: CheerReceived[];
}

/** The device's bookmark: the newest job it has offered and the newest cheer it has shown, ISO UTC; null for none yet. */
export interface CheerBookmark {
  jobs: string | null;
  cheered: string | null;
}

/** Where a device keeps its bookmark (localStorage on the web, AsyncStorage in the app). */
export const CHEER_SEEN_KEY = "op.cheerSeen";

export function parseCheerBookmark(raw: string | null | undefined): CheerBookmark {
  try {
    const v = raw ? (JSON.parse(raw) as Partial<CheerBookmark>) : null;
    return { jobs: typeof v?.jobs === "string" ? v.jobs : null, cheered: typeof v?.cheered === "string" ? v.cheered : null };
  } catch {
    return { jobs: null, cheered: null };
  }
}

/** The later of two ISO instants, either may be null. */
export function laterInstant(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}

/** One job of yours on the doer's sheet: its title and who cheered it, in the order they did. */
export interface CheeredJob {
  task_id: string;
  title: string;
  from: string[];
}

/**
 * What an open shows, from the news and the device's bookmark: the jobs to cheer (done after the
 * bookmark, newest first, at most CHEER_SHEET_MAX) and the doer's jobs (cheers after the bookmark,
 * grouped by job, the newest cheered first, at most CHEERED_SHEET_MAX), with the bookmark each sheet
 * moves to once closed: past every job and cheer in the news, so the ones over the cap are passed by
 * quietly and never come up later.
 */
export function cheerSheets<T extends { done_at: string | null }>(
  news: CheerNews<T>,
  seen: CheerBookmark,
): { jobs: T[]; jobsSeen: string | null; cheered: CheeredJob[]; cheeredSeen: string | null } {
  const after = (at: string | null, mark: string | null) => !!at && (!mark || Date.parse(at) > Date.parse(mark));
  const fresh = news.jobs.filter((t) => after(t.done_at, seen.jobs));
  const jobs = [...fresh].sort((a, b) => Date.parse(b.done_at!) - Date.parse(a.done_at!)).slice(0, CHEER_SHEET_MAX);
  const jobsSeen = fresh.reduce<string | null>((m, t) => laterInstant(m, t.done_at), seen.jobs);
  const newCheers = news.cheered.filter((c) => after(c.created_at, seen.cheered)).sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  const byTask = new Map<string, CheeredJob & { last: number }>();
  for (const c of newCheers) {
    const job = byTask.get(c.task_id) ?? { task_id: c.task_id, title: c.title, from: [], last: 0 };
    if (!job.from.some((n) => sameName(n, c.from_name))) job.from.push(normalizeName(c.from_name));
    job.last = Math.max(job.last, Date.parse(c.created_at));
    byTask.set(c.task_id, job);
  }
  const cheered = [...byTask.values()]
    .sort((a, b) => b.last - a.last)
    .slice(0, CHEERED_SHEET_MAX)
    .map(({ task_id, title, from }) => ({ task_id, title, from }));
  const cheeredSeen = newCheers.reduce<string | null>((m, c) => laterInstant(m, c.created_at), seen.cheered);
  return { jobs, jobsSeen, cheered, cheeredSeen };
}

// ---------- the confetti (docs/feel.md: hard frames, never eased) ----------

/** One piece of confetti: where it starts (fractions of the layer's width and height), how far it falls and drifts a frame (fractions too), its size in px and colour. */
export interface ConfettiPiece {
  x: number;
  y: number;
  fall: number;
  drift: number;
  size: number;
  fill: string;
}

/** The burst: this many pieces, this many frames, each this long; it then clears. */
export const CONFETTI_PIECES = 96;
export const CONFETTI_FRAMES = 22;
export const CONFETTI_FRAME_MS = 70;
/** The popper's colours and the app's gold, for the pieces. */
const CONFETTI_FILLS = ["#c8322a", "#7bc47f", "#6a9ee8", "#f6e7b0", "#e8b33a"];

/** A small seeded generator (mulberry32), so a burst is the same on every run of a test. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The burst's pieces: spread across the width from just above the top to under a third of the way down, falling past the bottom in about the burst's length. */
export function confettiPieces(seed: number, count = CONFETTI_PIECES): ConfettiPiece[] {
  const r = seeded(seed);
  return Array.from({ length: count }, () => ({
    x: r(),
    y: -0.35 + r() * 0.45,
    fall: (1 + r() * 0.6) / CONFETTI_FRAMES,
    drift: (r() - 0.5) * 0.02,
    size: [6, 8, 10][Math.floor(r() * 3)]!,
    fill: CONFETTI_FILLS[Math.floor(r() * CONFETTI_FILLS.length)]!,
  }));
}

/**
 * Where a piece is at `frame` (0 to CONFETTI_FRAMES - 1), in whole px of a `width` x `height` layer
 * (measured, never a percentage: docs/native.md), snapped to the art's 2px grid so it moves like a
 * sprite. Null once it has fallen out of the layer.
 */
export function confettiAt(p: ConfettiPiece, frame: number, width: number, height: number): { left: number; top: number } | null {
  const snap = (v: number) => Math.round(v / 2) * 2;
  const top = snap((p.y + p.fall * frame) * height);
  const left = snap((p.x + p.drift * frame) * width);
  if (top > height || left < -p.size || left > width) return null;
  return { left, top };
}

// ---------- the push (docs/product.md: the title says it, the body is a few words, no numbers) ----------

/** The push's title, to the peon who did it: "Cheered: Run Dishwasher". */
export function cheerPushTitle(taskTitle: string): string {
  return `Cheered: ${taskTitle}`;
}

/** The push's body: "From Hue." */
export function cheerPushBody(from: string): string {
  return `From ${normalizeName(from)}.`;
}

// ---------- refusals (the server's words, shown where the control is) ----------

/** 403: the peon it is credited to taps their own job (only through the API: they have no control). */
export const CHEER_OWN_JOB = "You can't cheer your own job.";

/** 409: the task is open (never finished, or reopened meanwhile). */
export const CHEER_NOT_DONE = "This task isn't done.";

/** 400: the request names nobody. */
export const CHEER_NO_NAME = "Who is cheering?";

// ---------- the icon (docs/cheers.md "The control") ----------

/**
 * The party popper beside the word, drawn in code like the claim hand and the note's heart: 9x9 art
 * pixels, a gold cone (its upper edge a shade darker) pointing down to the left with its cream mouth to
 * the upper right. Before you have cheered it is the popper alone; once you have, it has gone off, with
 * confetti in the household's colours over it. "." is transparent. Both clients draw it as crisp rects.
 */
export const CHEER_ICON: readonly string[] = [
  "..b....r.",
  "......g..",
  ".w......b",
  "...dw....",
  "..ddow.r.",
  "..dooow..",
  ".doooo..g",
  ".ooo.....",
  "oo.......",
];
export const CHEER_ICON_SIZE = 9;
/** o gold, d its shade, w the cream mouth (and a cream scrap); r, g and b the confetti. */
export const CHEER_ICON_COLOURS: Readonly<Record<string, string>> = {
  o: "#e8b33a",
  d: "#a8701c",
  w: "#f6e7b0",
  r: "#c8322a",
  g: "#7bc47f",
  b: "#6a9ee8",
};
/** The popper's own pixels; the rest of CHEER_ICON is the confetti it lets off. */
const POPPER = new Set(["o", "d"]);

/**
 * The icon as rects (x, y, colour), one per lit pixel, for an SVG on either client. `popped` false is
 * the popper alone (its body and its mouth); true adds the confetti.
 */
export function cheerIconPixels(popped: boolean): { x: number; y: number; fill: string }[] {
  const out: { x: number; y: number; fill: string }[] = [];
  CHEER_ICON.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      const fill = CHEER_ICON_COLOURS[ch];
      if (!fill) return;
      // The mouth is the cream cells touching the cone; a cream cell out in the air is confetti.
      const mouth = ch === "w" && [CHEER_ICON[y]?.[x - 1], CHEER_ICON[y + 1]?.[x], CHEER_ICON[y]?.[x + 1], CHEER_ICON[y - 1]?.[x]].some((c) => c !== undefined && POPPER.has(c));
      if (!popped && !POPPER.has(ch) && !mouth) return;
      out.push({ x, y, fill });
    }),
  );
  return out;
}

/** The pop when yours goes in: the note heart's three hard frames, no easing (docs/feel.md). */
export const CHEER_POP_FRAMES = HEART_POP_FRAMES;
export const CHEER_POP_FRAME_MS = HEART_POP_FRAME_MS;
